/**
 * Visit Tracking Utility
 * Records the visitor's journey (first visit, visit count, session landing page,
 * pages viewed with time on each) so every lead arrives with context.
 * Visitor data lives in localStorage (survives sessions), session data in sessionStorage.
 */

import { getStoredUtmParams, inferSourceParams } from '@/lib/utmTracking';

const VISITOR_STORAGE_KEY = 'horenko_visitor';
const SESSION_STORAGE_KEY = 'horenko_session';
const MAX_PAGES = 30;

function readStorage(storage, key) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeStorage(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Storage may be blocked (private mode) — tracking is best effort
  }
}

function currentPath() {
  return window.location.pathname + window.location.search;
}

/**
 * Registers a page view. Starts a new session (and counts a visit) when needed.
 * Should be called on page load and on every client-side route change.
 */
export function trackPageView() {
  if (typeof window === 'undefined') {
    return;
  }

  const now = Date.now();
  const path = currentPath();
  let session = readStorage(sessionStorage, SESSION_STORAGE_KEY);
  const visitor = readStorage(localStorage, VISITOR_STORAGE_KEY) || {
    firstVisitAt: now,
    visits: 0,
    firstTouch: null,
  };

  if (!session) {
    const source = { ...inferSourceParams(), ...getStoredUtmParams() };

    session = {
      startedAt: now,
      landing: path,
      referrer: document.referrer || '',
      pages: [],
    };

    visitor.visits += 1;
    visitor.firstTouch = visitor.firstTouch || {
      source: source.utm_source || '',
      medium: source.utm_medium || '',
      campaign: source.utm_campaign || '',
      landing: path,
      referrer: document.referrer || '',
      at: now,
    };
  }

  const last = session.pages[session.pages.length - 1];
  if (!last || last.p !== path) {
    session.pages.push({ p: path, t: now });
    if (session.pages.length > MAX_PAGES) {
      session.pages = session.pages.slice(-MAX_PAGES);
    }
  }

  visitor.lastSeenAt = now;
  writeStorage(sessionStorage, SESSION_STORAGE_KEY, session);
  writeStorage(localStorage, VISITOR_STORAGE_KEY, visitor);
}

function deviceType() {
  const width = window.innerWidth || screen.width;
  const touch = navigator.maxTouchPoints > 0;
  if (touch && width < 768) return 'mobile';
  if (touch && width < 1200) return 'tablet';
  return 'desktop';
}

/**
 * Builds the visit context sent together with a lead.
 * @returns {Object} Visitor journey summary
 */
export function getVisitContext() {
  if (typeof window === 'undefined') {
    return {};
  }

  const now = Date.now();
  const session = readStorage(sessionStorage, SESSION_STORAGE_KEY);
  const visitor = readStorage(localStorage, VISITOR_STORAGE_KEY);
  const pages = session?.pages || [];

  return {
    submittedAt: new Date(now).toISOString(),
    sessionStartedAt: session ? new Date(session.startedAt).toISOString() : null,
    timeOnSiteSec: session ? Math.round((now - session.startedAt) / 1000) : null,
    landing: session?.landing || null,
    referrer: session?.referrer || null,
    pagesViewed: pages.length,
    path: pages.map((page, index) => ({
      page: page.p,
      sec: Math.round(((pages[index + 1]?.t || now) - page.t) / 1000),
    })),
    visitNumber: visitor?.visits || 1,
    firstVisitAt: visitor ? new Date(visitor.firstVisitAt).toISOString() : null,
    firstTouch: visitor?.firstTouch
      ? { ...visitor.firstTouch, at: new Date(visitor.firstTouch.at).toISOString() }
      : null,
    device: deviceType(),
    screen: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language || '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
  };
}
